import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import GenericTable from '../../../common/GenericTable'
import SearchWithFilter from '../../../common/SearchWithFilter'
import FiltersModal, { FilterType } from '../../../common/FiltersModal'
import EmptyPageResults from '../../../common/EmptyPageResults'
import LoadingSpinner from '../../../common/LoadingSpinner'
import Dropdown from '../../../common/Dropdown'
import SimpleButton from '../../../common/SimpleButton'
import { toast } from '../../../common/Toast'
import TooltipedIcon from '../../../common/TooltipedIcon'
import { DownloadIcon, FilterTableIcon, InformationIcon } from '../../../../icons'
import { downloadFileUTF8, getMonthName, sortModelsAlphabetically } from '../../../../utils/functions'
import { useWindowSize } from '../../../../libs/hooks'
import { useDeepCompareEffect } from '../../../../hooks/useDeepCompareEffect'
import { loadModels, loadRegions } from '../../../../actions/shared'
import {
  clearReportsMessages,
  downloadRawSoldUnitsConnectionData,
  downloadStatisticSoldUnitsConnectionData,
  loadSoldDevicesCountries,
  loadSoldUnitsConnectionData,
} from '../../../../actions/reports'
import SoldUnitsConnectionChart from './SoldUnitsConnectionChart'
import '../../internal-reports.scss'

const orderingFields = [
  {
    field: 'model',
    order: 'asc',
    translationKey: 'model_asc',
  },
  {
    field: 'model',
    order: 'desc',
    translationKey: 'model_desc',
  },
  {
    field: 'year',
    order: 'asc',
    translationKey: 'year_asc',
  },
  {
    field: 'year',
    order: 'desc',
    translationKey: 'year_desc',
  },
  {
    field: 'month',
    order: 'asc',
    translationKey: 'month_asc',
  },
  {
    field: 'month',
    order: 'desc',
    translationKey: 'month_desc',
  },
  {
    field: 'soldCount',
    order: 'asc',
    translationKey: 'monthly_sold_asc',
  },
  {
    field: 'soldCount',
    order: 'desc',
    translationKey: 'monthly_sold_desc',
  },
  {
    field: 'soldConnectedCount',
    order: 'asc',
    translationKey: 'monthly_sold_connections_asc',
  },
  {
    field: 'soldConnectedCount',
    order: 'desc',
    translationKey: 'monthly_sold_connections_desc',
  },
  {
    field: 'connectedCount',
    order: 'asc',
    translationKey: 'monthly_total_connections_asc',
  },
  {
    field: 'connectedCount',
    order: 'desc',
    translationKey: 'monthly_total_connections_desc',
  },
]

const getCurrentYearStartISO = () => new Date(Date.UTC(new Date().getUTCFullYear(), 0, 1)).toISOString()

const SoldUnitsConnectionReports = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [soldUnitsConnectionTableData, setSoldUnitsConnectionTableData] = useState([])
  const [orderBy, setOrderBy] = useState(null)
  const [selectedFilterYear, setSelectedFilterYear] = useState(getCurrentYearStartISO())
  const [selectedFilterCountry, setSelectedFilterCountry] = useState([])
  const [selectedFilterModel, setSelectedFilterModel] = useState([])
  const [selectedFilterRegion, setSelectedFilterRegion] = useState([])
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [showChartFiltersModal, setShowChartFiltersModal] = useState(false)
  const [chartFrequency, setChartFrequency] = useState('month')
  const [chartType, setChartType] = useState('bar')
  const [expandedDevice, setExpandedDevice] = useState(0)
  const PAGE_SIZE = 10
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const chartTypeKeys = ['line', 'bar', 'stackedBar', 'pie']
  const chartFrequencyKeys = ['month', 'quarter', 'year']
  const hasTable = soldUnitsConnectionTableData.length > 0

  const chartFilterOptions = [
    {
      name: t('reports.frequency'),
      type: FilterType.singleSelect,
      dataset: chartFrequencyKeys,
      input: chartFrequency,
      output: (selected) => setChartFrequency(selected),
      required: true,
    },
    {
      name: t('reports.chart_type'),
      type: FilterType.singleSelect,
      dataset: chartTypeKeys,
      input: chartType,
      output: (selected) => setChartType(selected),
      required: true,
    },
  ]

  // useEffect(() => {
  //   if (selectedFilterCountry.length === 0 && selectedFilterRegion.length > 0) {
  //     setSelectedFilterRegion([])
  //   }
  // }, [selectedFilterCountry, selectedFilterRegion.length])

  useEffect(() => {
    if (props.deviceModels.length === 0) {
      props.actions.loadModels()
    }

    if (props.soldUnitsConnectionCountries.length === 0) {
      props.actions.loadSoldDevicesCountries()
    }

    if (props.reportsRegions.length === 0) {
      props.actions.loadRegions()
    }
  }, [props.actions])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)

      props.actions.clearReportsMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)

      props.actions.clearReportsMessages()
    }
  }, [props.successMessage])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadTableData(false)
    }
  }, [selectedPage])

  useDeepCompareEffect(() => {
    if (!shouldApplyFilters) return

    const t = window.setTimeout(() => {
      setSelectedPage(1)
      handleLoadTableData(true)
    })
    return () => clearTimeout(t)
  }, [selectedFilterCountry, selectedFilterModel, selectedFilterRegion, selectedFilterYear, orderBy, shouldApplyFilters])

  useEffect(() => {
    const tableData = props.soldUnitsConnectionTableData.map((device, index) => ({
      index,
      model: device.model,
      year: device.year,
      month: getMonthName(device.month),
      soldCount: device.ytdSold,
      soldConnectedCount: device.ytdSoldConnections,
      connectedCount: device.ytdTotalConnections,
    }))

    setSoldUnitsConnectionTableData(tableData)
    setVisibleCount(PAGE_SIZE)
  }, [props.soldUnitsConnectionTableData])

  const getUniqueRegions = (regions) => {
    if (!regions || regions.length === 0) {
      return []
    }

    const names = regions.map((region) => region.name)
    const uniqueNames = [...new Set(names)]
    return uniqueNames.sort((a, b) => a.localeCompare(b))
  }

  const createQueryParams = () => {
    let queryParams = {}

    if (selectedFilterModel.length > 0) {
      queryParams = { ...queryParams, model: selectedFilterModel }
    }

    if (selectedFilterRegion.length > 0) {
      queryParams = { ...queryParams, region: selectedFilterRegion }
    }

    if (selectedFilterCountry.length > 0) {
      const countryCodes = props.soldUnitsConnectionCountries
        .filter((c) => selectedFilterCountry.includes(c.name))
        .map((c) => c.countryCode)
        .filter(Boolean)

      if (countryCodes.length) {
        queryParams = { ...queryParams, country: countryCodes }
      }
    }

    if (selectedFilterYear) {
      const year = new Date(selectedFilterYear).getUTCFullYear()
      queryParams = { ...queryParams, year: year }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const handleLoadTableData = (withReset) => {
    let queryParams = createQueryParams()

    queryParams = {
      ...queryParams,
    }

    props.actions.loadSoldUnitsConnectionData(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleResetFilters = () => {
    setSelectedFilterModel([])
    setSelectedFilterRegion([])
    setSelectedFilterCountry([])
    setSelectedFilterYear(getCurrentYearStartISO())
    setVisibleCount(PAGE_SIZE)
  }

  const handleResetChartFilters = () => {
    setChartFrequency('')
    setChartType('')
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
    setVisibleCount(PAGE_SIZE)
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleDownloadStatistic = () => {
    let queryParams = createQueryParams()

    queryParams = {
      ...queryParams,
    }

    props.actions.downloadStatisticSoldUnitsConnectionData(queryParams).then((res) => {
      const date = new Date().toISOString().split('T')
      const dateNow = date[0].split('-').reverse().join('_')
      const timeNow = date[1].split('.')[0].split(':').join('_')

      downloadFileUTF8({
        data: res.value,
        fileName: `sold-units-connection_${dateNow}_${timeNow}.csv`,
        fileType: 'text/csv',
      })
    })
  }

  const handleDownloadRaw = () => {
    let queryParams = createQueryParams()

    queryParams = {
      ...queryParams,
    }

    props.actions.downloadRawSoldUnitsConnectionData(queryParams).then((res) => {
      const date = new Date().toISOString().split('T')
      const dateNow = date[0].split('-').reverse().join('_')
      const timeNow = date[1].split('.')[0].split(':').join('_')

      downloadFileUTF8({
        data: res.value,
        fileName: `sold-units-connection_raw_${dateNow}_${timeNow}.csv`,
        fileType: 'text/csv',
      })
    })
  }

  const renderChartSection = () => {
    return (
      <div className={`chart-section ${chartType === 'pie' ? 'pie-chart' : ''}`}>
        <div className="chart-filters-row">
          <div className="chart-title">{t('reports.sold_units_connection_chart')}</div>
          <SimpleButton className="filter-button" onClick={() => setShowChartFiltersModal(true)}>
            <div className="icon-wrapper">
              <FilterTableIcon />
            </div>
            {t('reports.chart_styles')}
          </SimpleButton>
        </div>
        <SoldUnitsConnectionChart
          data={soldUnitsConnectionTableData || []}
          chartType={chartType}
          chartFrequency={chartFrequency}
          key={`chart-${chartType}-${chartFrequency}`}
        />
        {showChartFiltersModal && (
          <div className="chart-filters-modal-container">
            <FiltersModal
              filters={chartFilterOptions}
              name="styles"
              closeFilters={() => setShowChartFiltersModal(false)}
              modalClassName="chart-filters-modal"
              resetFilters={handleResetChartFilters}
              requireAllRequiredFilters
            />
          </div>
        )}
      </div>
    )
  }

  const renderDevicesTable = () => {
    const rowsToRender = soldUnitsConnectionTableData.slice(0, visibleCount)

    if (!isMobile) {
      return (
        <>
          <GenericTable
            data={rowsToRender}
            headers={[
              {
                title: t('model'),
                orderKey: 'model',
              },
              {
                title: t('year'),
                orderKey: 'year',
              },
              {
                title: t('month'),
                orderKey: 'month',
              },
              {
                title: t('reports.monthly_sold'),
                orderKey: 'soldCount',
                infoIcon: t('reports.monthly_sold_info_icon'),
              },
              {
                title: t('reports.monthly_sold_connections'),
                orderKey: 'soldConnectedCount',
                infoIcon: t('reports.monthly_sold_connections_info_icon'),
              },
              {
                title: t('reports.monthly_total_connections'),
                orderKey: 'connectedCount',
                infoIcon: t('reports.monthly_total_connections_info_icon'),
              },
            ]}
            keys={['model', 'year', 'month', 'soldCount', 'soldConnectedCount', 'connectedCount']}
            activeSort={orderBy}
            onSort={(orderingField) => handleSort(orderingField)}
            isLoading={props.isLoading}
          />
          {visibleCount < soldUnitsConnectionTableData.length && (
            <div className="buttons-wrapper">
              <SimpleButton
                className="action-button"
                onClick={() => setVisibleCount((v) => Math.min(v + PAGE_SIZE, soldUnitsConnectionTableData.length))}
                disabled={props.isLoading}
              >
                {t('load_more', { defaultValue: 'Load more' })}
              </SimpleButton>
            </div>
          )}
        </>
      )
    }

    if (isMobile) {
      return (
        <>
          {soldUnitsConnectionTableData.length > 0 && (
            <div className="order-by-mobile-wrapper">
              <Dropdown
                className="dropdown-input"
                placeholder={t('select_ordering')}
                options={orderingFields.map((field) => ({
                  key: field.translationKey,
                  value: t(`sortable_context.${field.translationKey}`),
                }))}
                initialSelectedKey={orderBy?.translationKey}
                onChange={(orderBy) => {
                  const sortOption = orderingFields.find((f) => f.translationKey === orderBy.key)
                  handleSort(sortOption)
                }}
              />
            </div>
          )}

          {soldUnitsConnectionTableData.slice(0, visibleCount).map((device, index) => (
            <div key={index} className="card" onClick={() => setExpandedDevice(index)}>
              <div className={'card-item' + (expandedDevice !== index ? ' align-center' : '')}>
                <div className="card-item-title">{t('model')}</div>
                <div className={'card-item-body' + (expandedDevice !== index ? ' align-center' : '')}>
                  <div>{device.model}</div>
                </div>
              </div>
              {expandedDevice === index && (
                <>
                  <div className="card-item">
                    <div className="card-item-title">{t('year')}</div>
                    <div className="card-item-body">{device.year}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('month')}</div>
                    <div className="card-item-body">{device.month}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">
                      {t('reports.monthly_sold')}
                      <TooltipedIcon icon={<InformationIcon />} tooltipText={t('reports.monthly_sold_info_icon')} orientation="right" />
                    </div>
                    <div className="card-item-body">{device.soldCount}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">
                      {t('reports.monthly_sold_connections')}
                      <TooltipedIcon icon={<InformationIcon />} tooltipText={t('reports.monthly_sold_connections_info_icon')} orientation="right" />
                    </div>
                    <div className="card-item-body">{device.soldConnectedCount}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">
                      {t('reports.monthly_total_connections')}
                      <TooltipedIcon icon={<InformationIcon />} tooltipText={t('reports.monthly_total_connections_info_icon')} orientation="right" />
                    </div>
                    <div className="card-item-body">{device.connectedCount}</div>
                  </div>
                </>
              )}
            </div>
          ))}
          {visibleCount < soldUnitsConnectionTableData.length && (
            <div className="buttons-wrapper">
              <SimpleButton
                className="action-button"
                onClick={() => setVisibleCount((v) => Math.min(v + PAGE_SIZE, soldUnitsConnectionTableData.length))}
                disabled={props.isLoading}
              >
                {t('load_more', { defaultValue: 'Load more' })}
              </SimpleButton>
            </div>
          )}
        </>
      )
    }
  }

  const renderTableSection = () => (
    <div className="table-section">
      <div className="actions-wrapper">
        <div className="table-actions-row">
          <SimpleButton className="action-button" onClick={() => handleDownloadRaw()} disabled={props.isLoading || !hasTable}>
            <DownloadIcon />
            {t('reports.download_raw_data')}
          </SimpleButton>
          <SimpleButton className="action-button" onClick={() => handleDownloadStatistic()} disabled={props.isLoading || !hasTable}>
            <DownloadIcon />
            {t('reports.download_connection_report')}
          </SimpleButton>
        </div>
      </div>
      {renderDevicesTable()}
      {canLoadMore && (
        <div className="buttons-wrapper">
          <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
            {t('load_more')}
          </SimpleButton>
        </div>
      )}
    </div>
  )

  return (
    <div className="internal-reports-section sold-connection">
      <div className="actions-wrapper page-section">
        <SearchWithFilter
          showFiltersModal={() => setShowFiltersModal(true)}
          onResetFilters={() => handleResetFilters()}
          showCounterBadge={selectedFilterYear || selectedFilterModel.length > 0 || selectedFilterRegion.length > 0 || selectedFilterCountry.length > 0}
          counterBadge={selectedFilterModel.length + selectedFilterRegion.length + selectedFilterCountry.length + (selectedFilterYear ? 1 : 0)}
        />
      </div>
      <div className="disclaimer-box-text">
        <div className="icon-wrapper">
          <InformationIcon />
        </div>
        <div className="disclaimer-wrapper">
          {/* <div className="title">{t('reports.sold_chart_disclaimer')}</div> */}
          <div className="title">{t('reports.date_disclaimer')}</div>
        </div>
      </div>
      {!props.isLoading && !hasTable && !isFirstLoad ? (
        <EmptyPageResults title={t('no_data_found')} subtitle={t('cannot_find_matching_search')} />
      ) : (
        <>
          {renderChartSection()}
          {renderTableSection()}
        </>
      )}
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('year'),
              type: FilterType.dateFixed,
              dataset: null,
              input: selectedFilterYear,
              output: (filterYear) => {
                setSelectedFilterYear(filterYear)
              },
              yearPicker: true,
            },
            {
              name: t('model'),
              type: FilterType.multiSelect,
              dataset: props.deviceModels,
              input: selectedFilterModel,
              output: (model) => setSelectedFilterModel(model),
            },
            {
              name: t('country'),
              type: FilterType.multiSelect,
              dataset: props.soldUnitsConnectionCountries.map((c) => c.name),
              input: selectedFilterCountry,
              output: (country) => setSelectedFilterCountry(country),
            },
            {
              name: t('region'),
              type: FilterType.multiSelect,
              dataset: getUniqueRegions(props.reportsRegions),
              input: selectedFilterRegion,
              output: (region) => setSelectedFilterRegion(region),
            },
          ]}
          shouldFilter={(shouldApply) => {
            setShouldApplyFilters(shouldApply)
          }}
          resetFilters={handleResetFilters}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {props.isLoading && (
        <div className="spinner-wrapper">
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}

function stateToProps({ reports, shared }) {
  const rawModels = [...new Set(shared?.models?.filter((m) => m.ocp_access)?.map((model) => model.model) || [])]

  return {
    soldUnitsConnectionTableData: reports?.soldUnitsConnectionTableData || [],
    soldUnitsConnectionCountries: reports?.soldDevicesCountries || [],
    deviceModels: sortModelsAlphabetically(rawModels),
    reportsRegions: shared?.regions || [],
    isLoading: reports?.isSoldUnitsLoading,
    canLoadMore: reports?.canLoadMoreTableData,
    errorMessage: reports?.errorMessage,
    successMessage: reports?.successMessage,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadSoldUnitsConnectionData,
        loadSoldDevicesCountries,
        downloadRawSoldUnitsConnectionData,
        downloadStatisticSoldUnitsConnectionData,
        clearReportsMessages,
        loadRegions,
        loadModels,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(SoldUnitsConnectionReports)

